import { QueryClient, QueryFunction } from '@tanstack/react-query';
import {
  isOnline,
  addPendingAction,
  saveQueryToCache,
  loadQueryFromCache,
  type PendingAction,
} from './offlineCache';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Copy shown when there is nothing specific and safe to say. Callers that have
 * a localized string of their own pass it as `fallback`; this is the last
 * resort so that no code path can end up rendering raw server text instead.
 */
const GENERIC_API_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/** A server `message` longer than this is not user-facing copy — it is a dump. */
const MAX_SURFACED_MESSAGE_LENGTH = 300;

/**
 * Client-side mirror of `containsInternalDetail`
 * (`server/middleware/finalErrorHandler.ts`). The server already scrubs 4xx
 * messages, so this is defence in depth: a route that bypasses the catch-all
 * and hands us a driver error must still not reach a patient's screen. Kept
 * deliberately broad — a false positive costs a vaguer message, a false
 * negative leaks schema. All patterns are linear (no nested quantifiers).
 */
const INTERNAL_DETAIL_PATTERNS: RegExp[] = [
  /failed query/i, // Drizzle's wrapper text
  /\bparams:\s*\[/i, // ...and the bound parameters it appends
  /\bselect\b[\s\S]*\bfrom\b/i, // raw SQL of any shape
  /\binsert\s+into\b/i,
  /\bupdate\b[\s\S]*\bset\b/i,
  /\bdelete\s+from\b/i,
  /\b(?:relation|column|table)\s+"[^"]+"\s+does not exist/i,
  /\binvalid input syntax for type\b/i, // SQLSTATE 22007 / 22P02 — root causes F3, F4
  /\bduplicate key value violates\b/i,
  /\bconstraint\b\s+"/i,
  /\b(?:ECONNREFUSED|ENOTFOUND|ETIMEDOUT|ECONNRESET|EAI_AGAIN|EPIPE)\b/,
  /\b(?:postgres|postgresql|drizzle|neon\.tech|pg_[a-z_]+)\b/i,
  /\bat\s+\S+\s+\([^)]*:\d+:\d+\)/, // a stack frame pasted into a message
  /\/[\w-]+\/[\w-]+\.[a-z]{1,4}\b/i, // a server-side file path
  /\b[a-z][\w+.-]*:\/\/[^\s:@/]+:[^\s@/]+@/i, // a credentialed connection string
];

/** True when `message` looks like it came from a driver, ORM, or stack trace. */
function containsInternalDetail(message: string): boolean {
  return INTERNAL_DETAIL_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * A machine code (`CSRF_PROTECTION`, `NOT_FOUND`) rather than a sentence.
 * `auth-actions.ts:38` records why these are never rendered: users were shown
 * the literal string "CSRF_PROTECTION" in the sign-in banner.
 */
const MACHINE_CODE = /^[A-Z][A-Z0-9_]*$/; // flat, single quantifier — no backtracking

/**
 * Split the `"<status>: <body>"` string that `throwIfResNotOk` builds above
 * (`:10-15`) back into its two halves.
 *
 * Only a real `Error` produced by that function matches: anything else — a
 * `TypeError` from an unreachable network, a rejected non-Error value, the
 * `'Offline and no cached data available'` throw in `getQueryFn` — returns
 * `null`, which is what makes the "server rejected me" vs. "I could not reach
 * the server" distinction below possible.
 */
function parseHttpError(error: unknown): { status: number; body: string } | null {
  if (!(error instanceof Error)) {
    return null;
  }
  const match = /^(\d{3}): ?([\s\S]*)$/.exec(error.message);
  if (!match) {
    return null;
  }
  const status = Number(match[1]);
  if (status < 100 || status > 599) {
    return null;
  }
  return { status, body: match[2] };
}

/**
 * The HTTP status behind a failed `apiRequest` / `getQueryFn` call, or `null`
 * when the request never got a response at all.
 *
 * `apiRequest` rejects rather than returning a falsy `response.ok`, so call
 * sites cannot read `res.status` — they get an `Error` whose message is
 * `"<status>: <raw body>"` (see `throwIfResNotOk` at `:10-15`). This reads the
 * status back out of it.
 *
 * The `number | null` distinction is load-bearing, not cosmetic: a number means
 * the server answered and refused us, `null` means the server was never
 * reached. Patient-safety surfaces must tell a user which of the two happened
 * (FR-012) — a degraded drug-interaction check that reports "offline" when the
 * server actually rejected the request is a clinical-safety defect.
 */
export function apiErrorStatus(error: unknown): number | null {
  return parseHttpError(error)?.status ?? null;
}

/**
 * A safe, user-facing sentence for a failed `apiRequest` call.
 *
 * The direct analogue of `authErrorMessage` (`client/src/lib/auth-actions.ts`),
 * differing only in that it must first undo the `"<status>: <raw body>"`
 * packing that `throwIfResNotOk` applies (`:10-15`). Call sites used to do
 * `if (!response.ok) { const body = await response.json(); ... }` themselves;
 * once they move onto `apiRequest` they only have the thrown `Error`, and
 * rendering `error.message` directly would put a raw JSON body in a toast.
 *
 * Mirrors `finalErrorHandler`'s contract exactly (FR-017):
 *   - 4xx → the server's `message`, which handlers write to explain what the
 *     caller did wrong, but only if it survives the internal-detail scrubber.
 *   - 5xx → always `fallback`. A 5xx body may carry driver text, so it is
 *     never surfaced, no matter what it contains.
 *   - anything else (network failure, unparseable body, machine code only,
 *     blank message) → `fallback`.
 */
export function apiErrorMessage(
  error: unknown,
  fallback: string = GENERIC_API_ERROR_MESSAGE
): string {
  const safeFallback = fallback.trim().length > 0 ? fallback : GENERIC_API_ERROR_MESSAGE;
  const parsed = parseHttpError(error);
  if (!parsed || parsed.status < 400 || parsed.status > 499) {
    return safeFallback;
  }

  let body: unknown;
  try {
    body = JSON.parse(parsed.body);
  } catch {
    // A non-JSON body is either plain text from a proxy or an HTML error page.
    // Neither is copy we want to show, and neither can be trusted to be scrubbed.
    return safeFallback;
  }
  if (typeof body !== 'object' || body === null) {
    return safeFallback;
  }

  const { message, error: code } = body as { message?: unknown; error?: unknown };
  const candidate =
    typeof message === 'string' && message.trim().length > 0
      ? message.trim()
      : // `error` carries the machine code on the v1 envelope; fall back to it
        // only when it is a human sentence, never when it is a code.
        typeof code === 'string' && code.trim().length > 0 && !MACHINE_CODE.test(code.trim())
        ? code.trim()
        : '';

  if (!candidate || candidate.length > MAX_SURFACED_MESSAGE_LENGTH) {
    return safeFallback;
  }
  return containsInternalDetail(candidate) ? safeFallback : candidate;
}

/**
 * Make an API request with offline support
 * When offline, mutations are queued for later sync
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<Response> {
  // For non-GET requests when offline, queue for later
  if (!isOnline() && method !== 'GET') {
    const actionType = getActionTypeFromUrl(url, method);
    if (actionType) {
      await addPendingAction({
        type: actionType,
        payload: data as Record<string, unknown>,
      });
      // Return a fake success response for optimistic updates
      return new Response(
        JSON.stringify({
          success: true,
          offline: true,
          message: 'Queued for sync when online',
        }),
        {
          status: 202,
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }
  }

  // Check if data is FormData (for file uploads)
  const isFormData = data instanceof FormData;

  const headers: Record<string, string> = {
    'X-Requested-With': 'fetch', // CSRF protection
  };

  if (data && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Map URL and method to pending action type
 */
function getActionTypeFromUrl(url: string, method: string): PendingAction['type'] | null {
  if (url.includes('/appointments')) {
    return method === 'POST' ? 'CREATE_APPOINTMENT' : 'UPDATE_APPOINTMENT';
  }
  if (url.includes('/health-metrics')) {
    return 'CREATE_HEALTH_METRIC';
  }
  if (url.includes('/medications') && url.includes('adherence')) {
    return 'UPDATE_MEDICATION_ADHERENCE';
  }
  return null;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn = <T = unknown>(options: {
  on401: UnauthorizedBehavior;
}): QueryFunction<T> => {
  const { on401: unauthorizedBehavior } = options;

  return async ({ queryKey }) => {
    // The default queryFn builds the request URL by joining the key, so a
    // non-string segment (usually a filters object) silently produces
    // /path/[object Object] — a request no route serves. Fail loudly
    // instead: filters belong in the key string as query params, or the
    // query needs its own queryFn (QA 2026-07-31 M3).
    if (queryKey.some((segment) => typeof segment !== 'string' && typeof segment !== 'number')) {
      throw new Error(
        `Default queryFn got a non-string queryKey segment (${queryKey
          .map((segment) => typeof segment)
          .join(', ')}). Encode filters into the URL string or pass a custom queryFn.`
      );
    }
    const cacheKey = queryKey.join('/');

    // If offline, try to return cached data
    if (!isOnline()) {
      const cachedData = await loadQueryFromCache(cacheKey);
      if (cachedData !== null) {
        console.warn('[QueryClient] Returning cached data for:', cacheKey);
        return cachedData as T;
      }
      throw new Error('Offline and no cached data available');
    }

    try {
      const res = await fetch(cacheKey as string, {
        headers: {
          'X-Requested-With': 'fetch', // CSRF protection
        },
        credentials: 'include',
      });

      if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
        // Drain the body: an unread response stays in flight in Chromium, so
        // the page never reaches network idle (visual tests time out on it).
        await res.text();
        return null as T;
      }

      await throwIfResNotOk(res);
      const data = await res.json();

      // Cache successful responses for offline use
      if (data !== null) {
        await saveQueryToCache(cacheKey, data);
      }

      return data as T;
    } catch (error) {
      // On network error, try to return cached data
      if (!isOnline() || (error instanceof TypeError && error.message.includes('fetch'))) {
        const cachedData = await loadQueryFromCache(cacheKey);
        if (cachedData !== null) {
          console.warn('[QueryClient] Network error, returning cached data for:', cacheKey);
          return cachedData as T;
        }
      }
      throw error;
    }
  };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry 401/403 errors. Read the status off the front of the
        // thrown message rather than substring-matching it: a 500 whose body
        // happens to contain "403" is a transient failure and should retry.
        const status = apiErrorStatus(error);
        if (status === 401 || status === 403) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
